import {
  Autocomplete,
  type AutocompleteProps,
  TextField,
  type TextFieldProps,
  type UseAutocompleteProps,
} from "@mui/material";
import type { APIError, SelectOption } from "core/entities";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ErrorComponent } from "../ErrorComponent";

export type QueryParamMultiSelectProps = {
  queryKey: string;
  options: SelectOption[];
  label: string;
  placeholder?: string;
  loading?: boolean;
  apiError?: APIError | undefined;
  onChangeCallBack?: UseAutocompleteProps<
    SelectOption,
    true,
    false,
    false
  >["onChange"];
  textFieldProps?: TextFieldProps;
} & Omit<AutocompleteProps<SelectOption, true, false, false>, "renderInput">;

export function QueryParamMultiSelect({
  queryKey,
  label,
  placeholder,
  options,
  loading,
  apiError,
  onChangeCallBack,
  textFieldProps,
  ...autocompleteProps
}: QueryParamMultiSelectProps) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:QueryParamMultiSelect",
  });
  const [queryParam, setQueryParam] = useQueryState(
    queryKey,
    parseAsArrayOf(parseAsString)
  );

  const selectedOptions = useMemo(() => {
    if (!queryParam) {
      return [];
    }
    return options.filter((option) => queryParam.includes(option.value));
  }, [queryParam, options]);

  return (
    <Autocomplete
      renderInput={(params) => {
        if (apiError) {
          return <ErrorComponent apiError={apiError} />;
        }
        return (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            {...textFieldProps}
          />
        );
      }}
      limitTags={1}
      getOptionKey={(op) => op?.value ?? op?.label ?? op}
      options={options}
      noOptionsText={t("noOptions")}
      value={selectedOptions}
      multiple
      loading={loading}
      onChange={(event, newOptions, reason) => {
        switch (reason) {
          case "selectOption":
            if (!newOptions || newOptions.length === 0) {
              break;
            }
            setQueryParam(newOptions.map((option) => option.value));
            break;
          case "removeOption":
            if (!newOptions || newOptions.length === 0) {
              setQueryParam(null);
              break;
            }
            setQueryParam(newOptions.map((option) => option.value));
            break;
          case "clear":
            setQueryParam(null);
            break;
          case "blur":
            break;
          case "createOption":
            break;
          default:
            exhaustiveCheck(reason);
            // eslint-disable-next-line no-console
            console.error(`Unhandled reason: ${reason}`);
        }
        if (typeof onChangeCallBack === "function") {
          onChangeCallBack(event, newOptions, reason);
        }
      }}
      {...autocompleteProps}
    />
  );
}
